import { useState, useEffect, useRef } from "react";
import PageLayout from "@/components/feature/PageLayout";
import DiagonalLines from "@/components/feature/DiagonalLines";
import HomeFooter from "@/pages/home/components/HomeFooter";

type FormState = "idle" | "submitting" | "success" | "error";

export default function ContactPage() {
  const [formState, setFormState] = useState<FormState>("idle");
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(40px)";
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        el.style.transition = "opacity 0.9s cubic-bezier(0.22,1,0.36,1), transform 0.9s cubic-bezier(0.22,1,0.36,1)";
        el.style.opacity = "1";
        el.style.transform = "translateY(0)";
        obs.disconnect();
      }
    }, { threshold: 0.08 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value ?? "";
    if (message.length > 500) return;

    setFormState("submitting");
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([k, v]) => data.append(k, v as string));

    try {
      const res = await fetch("https://formspree.io/f/mreoqgrv", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: data.toString(),
      });
      if (res.ok) {
        setFormState("success");
        form.reset();
        setCharCount(0);
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "#f8f8f8",
    border: "1px solid #e0e0e0",
    padding: "16px 20px",
    fontFamily: "'Noto Sans JP', sans-serif",
    fontSize: "16px",
    color: "#111111",
    outline: "none",
    transition: "border-color 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: "'Noto Sans JP', sans-serif",
    fontSize: "14px",
    fontWeight: 700,
    color: "#111111",
    letterSpacing: "0.04em",
    display: "block",
    marginBottom: "10px",
  };

  return (
    <PageLayout>
      <section className="py-16 md:py-28 px-4 md:px-6 lg:px-20" style={{ background: "#ffffff" }}>
        <div ref={sectionRef} className="max-w-6xl mx-auto">

          <div className="grid grid-cols-1 md:grid-cols-7 gap-10 md:gap-16 mt-12 md:mt-20">
            {/* Left: description */}
            <div className="md:col-span-2 flex flex-col justify-start pt-2">
              <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: "13px", letterSpacing: "0.4em", color: "#c00000", marginBottom: "16px" }}>
                CONTACT FORM
              </p>
              <h2
                className="font-bold mb-8"
                style={{ fontFamily: "'Barlow', sans-serif", fontSize: "clamp(48px, 6.5vw, 72px)", color: "#111111", letterSpacing: "-0.02em", lineHeight: 1.05 }}
              >
                Contact
              </h2>
              <div className="mb-8" style={{ width: "40px", height: "3px", background: "#c00000" }} />
              <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "16px", color: "#111111", lineHeight: 1.8 }}>
                サービスに関するご質問・ご相談、お見積もりのご依頼など、どんな小さなことでもお気軽にご連絡ください。<br /><br />
                通常2営業日以内にご返信いたします。
              </p>

              <div className="mt-12 flex flex-col gap-5">
                {[
                  "ホームページ制作のご相談",
                  "SEO・MEO・LLMO対策について",
                  "SNS・広告運用のご依頼",
                  "料金・プランのご確認",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-4">
                    <div className="w-6 h-6 flex items-center justify-center shrink-0" style={{ background: "#c00000" }}>
                      <i className="ri-check-line text-sm" style={{ color: "#ffffff" }} />
                    </div>
                    <span style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "16px", color: "#111111" }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: form */}
            <div className="md:col-span-5">
              {formState === "success" ? (
                <div
                  className="flex flex-col items-center justify-center py-20 gap-6"
                  style={{ border: "1px solid #e8e8e8", background: "#fafafa" }}
                >
                  <div className="w-16 h-16 flex items-center justify-center" style={{ background: "#c00000" }}>
                    <i className="ri-check-line text-2xl" style={{ color: "#ffffff" }} />
                  </div>
                  <div className="text-center">
                    <h3 className="font-bold mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "18px", color: "#111111" }}>
                      送信完了しました
                    </h3>
                    <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "14px", color: "#111111", lineHeight: 1.75 }}>
                      お問い合わせありがとうございます。<br />
                      2営業日以内にご返信いたします。
                    </p>
                  </div>
                  <button
                    onClick={() => setFormState("idle")}
                    className="px-8 py-3 cursor-pointer whitespace-nowrap"
                    style={{ background: "#f0f0f0", color: "#111111", fontFamily: "'Noto Sans JP', sans-serif", fontSize: "14px", border: "none" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "#c00000")}
                    onMouseLeave={e => (e.currentTarget.style.background = "#111111")}
                  >
                    別のお問い合わせをする
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-8"
                  style={{ border: "1px solid #e8e8e8", background: "#fafafa", padding: "clamp(28px, 5vw, 48px)" }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    <div>
                      <label style={labelStyle}>
                        お名前 <span style={{ color: "#c00000" }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="山田 太郎"
                        style={inputStyle}
                        onFocus={e => (e.currentTarget.style.borderColor = "#c00000")}
                        onBlur={e => (e.currentTarget.style.borderColor = "#e0e0e0")}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>
                        会社名
                      </label>
                      <input
                        type="text"
                        name="company"
                        placeholder="株式会社〇〇"
                        style={inputStyle}
                        onFocus={e => (e.currentTarget.style.borderColor = "#c00000")}
                        onBlur={e => (e.currentTarget.style.borderColor = "#e0e0e0")}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={labelStyle}>
                      メールアドレス <span style={{ color: "#c00000" }}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="example@email.com"
                      style={inputStyle}
                      onFocus={e => (e.currentTarget.style.borderColor = "#c00000")}
                      onBlur={e => (e.currentTarget.style.borderColor = "#e0e0e0")}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>電話番号</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="052-XXX-XXXX"
                      style={inputStyle}
                      onFocus={e => (e.currentTarget.style.borderColor = "#c00000")}
                      onBlur={e => (e.currentTarget.style.borderColor = "#e0e0e0")}
                    />
                  </div>

                  <div>
                    <label style={labelStyle}>
                      お問い合わせ種別 <span style={{ color: "#c00000" }}>*</span>
                    </label>
                    <select
                      name="category"
                      required
                      style={{ ...inputStyle, cursor: "pointer" }}
                      onFocus={e => (e.currentTarget.style.borderColor = "#c00000")}
                      onBlur={e => (e.currentTarget.style.borderColor = "#e0e0e0")}
                    >
                      <option value="">選択してください</option>
                      <option value="ホームページ制作">ホームページ制作</option>
                      <option value="SEO対策">SEO対策</option>
                      <option value="MEO対策">MEO対策</option>
                      <option value="SNS運用">SNS運用</option>
                      <option value="広告運用">広告運用</option>
                      <option value="料金・プランについて">料金・プランについて</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>
                      お問い合わせ内容 <span style={{ color: "#c00000" }}>*</span>
                    </label>
                    <div className="relative">
                      <textarea
                        name="message"
                        required
                        rows={6}
                        maxLength={500}
                        placeholder="ご相談内容をご記入ください（500文字以内）"
                        style={{ ...inputStyle, resize: "vertical", minHeight: "180px" }}
                        onFocus={e => (e.currentTarget.style.borderColor = "#c00000")}
                        onBlur={e => (e.currentTarget.style.borderColor = "#e0e0e0")}
                        onChange={e => setCharCount(e.currentTarget.value.length)}
                      />
                      <span
                        className="absolute bottom-3 right-3 text-xs"
                        style={{ fontFamily: "'DM Mono', monospace", color: charCount > 450 ? "#c00000" : "#111111" }}
                      >
                        {charCount}/500
                      </span>
                    </div>
                  </div>

                  {formState === "error" && (
                    <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "14px", color: "#c00000" }}>
                      送信に失敗しました。時間をおいて再度お試しください。
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="w-full py-5 flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap transition-all duration-300"
                    style={{
                      background: formState === "submitting" ? "#111111" : "#111111",
                      color: "#ffffff",
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontSize: "16px",
                      fontWeight: 700,
                      border: "none",
                      letterSpacing: "0.08em",
                    }}
                    onMouseEnter={e => { if (formState !== "submitting") (e.currentTarget as HTMLButtonElement).style.background = "#c00000"; }}
                    onMouseLeave={e => { if (formState !== "submitting") (e.currentTarget as HTMLButtonElement).style.background = "#111111"; }}
                  >
                    {formState === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        送信中...
                      </>
                    ) : (
                      <>
                        送信する
                        <i className="ri-arrow-right-line" />
                      </>
                    )}
                  </button>

                  <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "13px", color: "#111111", textAlign: "center", lineHeight: 1.8 }}>
                    送信いただいた個人情報は、お問い合わせへの回答のみに使用します。<br />
                    詳しくは<button type="button" style={{ color: "#c00000", background: "none", border: "none", cursor: "pointer", fontSize: "13px", padding: 0 }}>プライバシーポリシー</button>をご確認ください。
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <HomeFooter />
      <DiagonalLines variant="gold" intensity="light" />
    </PageLayout>
  );
}
